/**
 * @file PrivacyPolicy.tsx
 * @path apps/website/src/pages/legal/PrivacyPolicy.tsx
 * @description
 */

import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { SeoHelmet as SEOHelmet } from "@/components";
import { NAV_LINKS, siteMetadata } from "@/config";
import { formatPhone } from "@sirbastians/utils";

const PrivacyPolicy = () => {
  return (
    <>
      <SEOHelmet
        pageType="default"
        title="Privacy Policy | Sir Bastian's Shuttle Service"
        description="Sir Bastian's Shuttle Service privacy policy. Learn how we collect, use, and protect your personal information."
        url="/privacy-policy"
      />

      <div className="min-h-screen bg-background py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          {/* Back Button */}
          <Link
            to="/"
            className="inline-flex items-center text-sm text-muted-foreground hover:text-foreground mb-8 transition-colors"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Home
          </Link>

          {/* Header */}
          <header className="mb-12">
            <h1 className="text-4xl font-bold mb-4">Privacy Policy</h1>
            <p className="text-muted-foreground">
              Last updated: October 3, 2025
            </p>
          </header>

          {/* Content */}
          <div className="prose prose-slate max-w-none space-y-8">
            <section>
              <h2 className="text-2xl font-semibold mb-4">1. Introduction</h2>
              <p className="text-muted-foreground leading-relaxed">
                Sir Bastian's Shuttle Service ("we," "us," or "our") is
                committed to protecting your privacy. This Privacy Policy
                explains how we collect, use, disclose, and safeguard your
                information when you use our website and services.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-4">
                2. Information We Collect
              </h2>
              <h3 className="text-xl font-semibold mb-3 mt-6">
                2.1 Personal Information
              </h3>
              <p className="text-muted-foreground leading-relaxed mb-4">
                We may collect personal information that you provide directly to
                us, including:
              </p>
              <ul className="list-disc pl-6 space-y-2 text-muted-foreground">
                <li>Name and contact details (email, phone number)</li>
                <li>Booking and travel information</li>
                <li>
                  Payment information (processed securely through third-party
                  payment processors)
                </li>
                <li>
                  Job application details (CV, qualifications, references)
                </li>
              </ul>

              <h3 className="text-xl font-semibold mb-3 mt-6">
                2.2 Automatically Collected Information
              </h3>
              <p className="text-muted-foreground leading-relaxed mb-4">
                When you visit our website, we may automatically collect:
              </p>
              <ul className="list-disc pl-6 space-y-2 text-muted-foreground">
                <li>Browser type and version</li>
                <li>Device information</li>
                <li>IP address</li>
                <li>Usage data and analytics</li>
              </ul>

              <h3 className="text-xl font-semibold mb-3 mt-6">2.3 Cookies</h3>
              <p className="text-muted-foreground leading-relaxed">
                We use cookies and similar tracking technologies to enhance your
                browsing experience. We use functional cookies to remember your
                preferences (such as sidebar state) to improve usability.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-4">
                3. How We Use Your Information
              </h2>
              <p className="text-muted-foreground leading-relaxed mb-4">
                We use the information we collect to:
              </p>
              <ul className="list-disc pl-6 space-y-2 text-muted-foreground">
                <li>Provide and manage our shuttle and tour services</li>
                <li>Process bookings and payments</li>
                <li>Respond to inquiries and provide customer support</li>
                <li>Process job applications</li>
                <li>Improve our website and services</li>
                <li>Send service-related communications</li>
                <li>Comply with legal obligations</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-4">
                4. Information Sharing
              </h2>
              <p className="text-muted-foreground leading-relaxed mb-4">
                We do not sell your personal information. We may share your
                information with:
              </p>
              <ul className="list-disc pl-6 space-y-2 text-muted-foreground">
                <li>
                  <strong>Service Providers:</strong> Third-party companies that
                  help us operate our business (e.g., Formspree for form
                  processing, payment processors)
                </li>
                <li>
                  <strong>Legal Requirements:</strong> When required by law or
                  to protect our rights
                </li>
                <li>
                  <strong>Business Transfers:</strong> In connection with a
                  merger, sale, or acquisition
                </li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-4">5. Data Security</h2>
              <p className="text-muted-foreground leading-relaxed">
                We implement appropriate technical and organizational measures
                to protect your personal information. However, no method of
                transmission over the internet is 100% secure, and we cannot
                guarantee absolute security.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-4">6. Your Rights</h2>
              <p className="text-muted-foreground leading-relaxed mb-4">
                Under the Protection of Personal Information Act (POPIA) and
                other applicable laws, you have the right to:
              </p>
              <ul className="list-disc pl-6 space-y-2 text-muted-foreground">
                <li>Access your personal information</li>
                <li>Correct inaccurate information</li>
                <li>Request deletion of your information</li>
                <li>Object to processing of your information</li>
                <li>Withdraw consent (where applicable)</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-4">
                7. Children's Privacy
              </h2>
              <p className="text-muted-foreground leading-relaxed">
                Our services are not directed to children under 18. We do not
                knowingly collect personal information from children. If you
                believe we have collected information from a child, please
                contact us immediately.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-4">
                8. Changes to This Policy
              </h2>
              <p className="text-muted-foreground leading-relaxed">
                We may update this Privacy Policy from time to time. The updated
                version will be indicated by an updated "Last updated" date. We
                encourage you to review this policy periodically.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-4">9. Contact Us</h2>
              <p className="text-muted-foreground leading-relaxed mb-4">
                If you have questions about this Privacy Policy or our data
                practices, please contact us:
              </p>
              <div className="bg-muted/50 p-6 rounded-lg">
                <p className="text-muted-foreground">
                  <strong>
                    {siteMetadata.title}
                    <br />
                  </strong>
                  <br />
                  Email: {siteMetadata.email}
                  <br />
                  Phone: {formatPhone(siteMetadata.phone)}
                  <br />
                  Address: Cape Town, South Africa
                </p>
              </div>
            </section>
          </div>

          {/* Footer Links */}
          <div className="mt-12 pt-8 border-t border-border">
            <div className="flex flex-wrap gap-6 text-sm">
              <Link
                to={NAV_LINKS.experiences.path}
                className="text-muted-foreground hover:text-foreground transition-colors"
              >
                Experiences
              </Link>
              <Link
                to="/terms-of-service"
                className="text-muted-foreground hover:text-foreground transition-colors"
              >
                Terms of Service
              </Link>
              <Link
                to={NAV_LINKS.contactUs.path}
                className="text-muted-foreground hover:text-foreground transition-colors"
              >
                Contact Us
              </Link>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default PrivacyPolicy;
